import {
  Box,
  Button,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Modal,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@material-ui/core';
import Select from '@material-ui/core/Select';
import React, { FC, useMemo, useState } from 'react';
import { slices, useAppSelector } from 'src/redux';
import { formatCurrency, copyToClipboard } from 'src/utils';
import { colors } from 'src/constants';
import { Link } from '@material-ui/core';
import { AlternatingColorTableRow } from 'src/components/table';
import { useIsMobile } from 'src/hooks/use-is-mobile';
import CloseIcon from '@material-ui/icons/Close';
import { PrintTransactionsWithBalanceModal } from './PrintTransactionsWithBalanceModal';
import PrintIcon from '@material-ui/icons/Print';
import { filterByOption } from 'src/utils/sales/filterByOption';
import useResolution from 'src/hooks/useResolution';
import { AlternateRowContainer } from 'src/views/product/component/AlternateRowContainer';
import { TextRow } from 'src/views/product/component/TextRow';
import { LinkComponent } from 'src/components/link';
const { selectors: salesSelectors } = slices.sales;
import FileCopyIcon from '@material-ui/icons/FileCopy';

interface Props {
  open?: boolean;
  totalBalance?: string;
  handleClose: () => void;
}

export const TransactionsWithBalanceModal: FC<Props> = ({
  open = true,
  handleClose
}) => {
  const isMobile = useIsMobile();
  const { width, isLandscape } = useResolution();

  const [isPrintOpen, setIsPrintOpen] = useState<boolean>(false);
  const [filterOption, setFilterOption] = useState<string>('all');

  const sales = useAppSelector(salesSelectors.selectAllSales);

  const formattedSales = useMemo(() => {
    return (sales || [])
      .map((data) => ({
        transaction_no: data?.transaction_no,
        order_no: data?.order_no,
        customer_name: data?.customer_name,
        branch: data?.branch,
        total_amount: data?.total_retail_price,
        total_payment: (data?.payments || []).reduce(
          (accumulator, current) => accumulator + (current?.amount || 0),
          0
        ),
        balance: data?.balance
      }))
      .filter((data: any) => filterByOption(data, filterOption));
  }, [sales, filterOption]);

  const totalSalesBalance = useMemo(() => {
    const filtered = sales.filter((data: any) =>
      filterByOption(data, filterOption)
    );

    return filtered?.reduce(
      (accumulator: any, current: any) => accumulator + current?.balance,
      0
    );
  }, [filterOption, sales]);

  const handleFilterChange = (e: React.ChangeEvent<{ value: any }>) => {
    setFilterOption(e.target.value);
  };

  const onHandleCopyTransactionNo = (transaction?: string) => {
    copyToClipboard(transaction || '');
  };

  return (
    <>
      <PrintTransactionsWithBalanceModal
        isOpen={isPrintOpen}
        handleClose={() => setIsPrintOpen(false)}
        filterOption={filterOption}
      />
      <Modal
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center'
        }}
        open={open}
        onClose={handleClose}
      >
        <Box
          style={{
            background: 'white',
            width: isMobile ? '95vw' : 'auto',
            maxHeight: isMobile ? '90dvh' : '100dvh',
            overflow: 'hidden',
            borderRadius: 8
          }}
        >
          <DialogTitle>
            <Box
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                overflow: 'visible'
              }}
            >
              <Typography variant="h5" style={{ width: '13em' }}>
                Transactions with Balance
              </Typography>
              <CloseIcon color="secondary" onClick={handleClose} />
            </Box>
            {isMobile && !isLandscape ? (
              <Box
                display="flex"
                flexDirection="row"
                alignItems="center"
                style={{ marginTop: 5 }}
              >
                <FormControl size="small" variant="outlined">
                  <InputLabel id="filter-option-label">Filter</InputLabel>
                  <Select
                    labelId="filter-option-label"
                    value={filterOption}
                    onChange={handleFilterChange}
                    label="Filter"
                    style={{ width: '160px' }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="balance">Balance</MenuItem>
                    <MenuItem value="overpayment">Over Payments</MenuItem>
                  </Select>
                </FormControl>
              </Box>
            ) : (
              <Box display="flex" flexDirection="row" alignItems="center">
                <RadioGroup
                  row
                  value={filterOption}
                  onChange={(e) => setFilterOption(e.target.value)}
                >
                  <FormControlLabel
                    value="all"
                    control={<Radio />}
                    label="All"
                  />
                  <FormControlLabel
                    value="balance"
                    control={<Radio />}
                    label="Balance"
                  />
                  <FormControlLabel
                    value="overpayment"
                    control={<Radio />}
                    label="Over Payments"
                  />
                </RadioGroup>
              </Box>
            )}
          </DialogTitle>
          <DialogContent
            style={{
              maxHeight: isMobile ? '70dvh' : 'auto',
              overflowY: 'auto',
              paddingBottom: isMobile
                ? width < 400
                  ? '80px'
                  : isLandscape
                  ? '110px'
                  : undefined
                : '0'
            }}
          >
            {isMobile ? (
              <>
                {formattedSales?.length > 0 &&
                  formattedSales.map((i, index) => (
                    <AlternateRowContainer index={index} key={i.transaction_no}>
                      <div style={{ padding: '1em' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between'
                          }}
                        >
                          <LinkComponent
                            openInNewTab
                            href={`/app/transaction/${i.transaction_no}`}
                            title={i.transaction_no || ''}
                            font="Roboto, Helvetica, Arial, sans-serif"
                          />
                          <FileCopyIcon
                            style={{ cursor: 'pointer', marginLeft: '15px' }}
                            onClick={() =>
                              onHandleCopyTransactionNo(i.transaction_no)
                            }
                          />
                        </Box>

                        <Divider style={{ marginTop: '10px' }} />

                        <TextRow
                          label="Warranty No"
                          value={i.order_no || '--'}
                          padding={'8px 0'}
                          noBorderBottom
                        />

                        <TextRow
                          label="Sold to"
                          value={i.customer_name || '--'}
                          padding={'8px 0'}
                          noBorderBottom
                        />

                        <TextRow
                          label="Branch"
                          value={i.branch || '--'}
                          padding={'8px 0'}
                          noBorderBottom
                        />

                        <TextRow
                          label="Amount"
                          component={
                            <Typography style={{ color: colors.blue[600] }}>
                              {formatCurrency(i.total_amount)}
                            </Typography>
                          }
                          padding={'8px 0'}
                          noBorderBottom
                        />

                        <TextRow
                          label="Paid"
                          component={
                            <Typography style={{ color: colors.purple[600] }}>
                              {formatCurrency(i.total_payment)}
                            </Typography>
                          }
                          padding={'8px 0'}
                          noBorderBottom
                        />

                        <TextRow
                          label="Balance"
                          component={
                            <Typography style={{ color: colors.red[600] }}>
                              {formatCurrency(i.balance)}
                            </Typography>
                          }
                          padding={'8px 0'}
                          noBorderBottom
                        />
                      </div>
                    </AlternateRowContainer>
                  ))}
              </>
            ) : (
              <Table
                stickyHeader
                style={{
                  display: 'block',
                  maxHeight: '50vh',
                  overflowY: 'auto'
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell style={{ padding: '4px' }}>
                      <Typography style={{ fontWeight: 'bold' }}>
                        Transaction No.
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        padding: '6px',
                        width: '10em'
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        Warranty No.
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        padding: '6px',
                        width: '10em'
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        Customer Name
                      </Typography>
                    </TableCell>

                    <TableCell style={{ textAlign: 'center', padding: '4px' }}>
                      <Typography style={{ fontWeight: 'bold', width: '10em' }}>
                        Branch
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        width: '10em',
                        textAlign: 'center',
                        padding: '4px'
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        Total Amount
                      </Typography>
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center',
                        padding: '6px',
                        width: '10em'
                      }}
                    >
                      <Typography style={{ fontWeight: 'bold' }}>
                        Amount Paid
                      </Typography>
                    </TableCell>
                    <TableCell style={{ textAlign: 'center', padding: '4px' }}>
                      <Typography style={{ fontWeight: 'bold' }}>
                        Balance Remaining
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formattedSales[0] &&
                    formattedSales?.map((i) => (
                      <AlternatingColorTableRow
                        hover
                        key={i.transaction_no}
                        style={{ height: '5em' }}
                      >
                        <TableCell style={{ padding: '4px' }}>
                          <Link
                            href={`/app/transaction/${i.transaction_no}`}
                            target={'_blank'}
                            rel={'noopener noreferrer'}
                          >
                            {i.transaction_no}
                          </Link>
                        </TableCell>
                        <TableCell style={{ padding: '4px' }}>
                          <Typography style={{ textAlign: 'center' }}>
                            {i?.order_no}
                          </Typography>
                        </TableCell>
                        <TableCell style={{ padding: '4px' }}>
                          {i?.customer_name}
                        </TableCell>
                        <TableCell
                          style={{
                            width: '10em',
                            textAlign: 'center',
                            padding: '6px'
                          }}
                        >
                          <Typography>{i.branch}</Typography>
                        </TableCell>
                        <TableCell
                          style={{ textAlign: 'center', padding: '6px' }}
                        >
                          <Typography style={{ color: colors.blue[600] }}>
                            {formatCurrency(i.total_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{ textAlign: 'center', padding: '6px' }}
                        >
                          <Typography style={{ color: colors.purple[600] }}>
                            {formatCurrency(i.total_payment)}
                          </Typography>
                        </TableCell>
                        <TableCell
                          style={{ textAlign: 'center', padding: '4px' }}
                        >
                          <Typography style={{ color: colors.red[600] }}>
                            {formatCurrency(i.balance)}
                          </Typography>
                        </TableCell>
                      </AlternatingColorTableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </DialogContent>
          <Box
            style={{
              position: 'sticky',
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
              rowGap: isMobile ? '0.5em' : '0em',
              alignItems: 'center',
              columnGap: '1em',
              borderTop: '1px solid #ddd',
              background: 'white',
              bottom: 0,
              padding: '10px'
            }}
          >
            <Button
              startIcon={<PrintIcon />}
              color="primary"
              variant="contained"
              onClick={() => setIsPrintOpen(true)}
              style={{ width: 200 }}
            >
              Print
            </Button>

            <Box
              display="flex"
              flexDirection="row"
              style={{
                width: isMobile ? '100%' : 'auto',
                justifyContent: 'flex-end'
              }}
            >
              <Typography style={{ fontWeight: 'bold' }}>
                Total Balance:
              </Typography>
              <Typography color="secondary" style={{ marginLeft: '5px' }}>
                {formatCurrency(totalSalesBalance)}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
};
